import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../app.js";
import { Store, Settings } from "../utils/store.js";
import { Api } from "../api.js";
import { User } from "../model/user.js";
import { ApiKey, MAX_API_KEYS } from "../model/api_key.js";

import "../components/dashboard-navbar-component.js";
import "../components/dashboard-drawer-component.js";

@customElement("settings-page")
export class SettingsPage extends BaseElement {
    @state()
    private settings: Settings = Store.getSettings();
    @state()
    private user: User | null = null;
    @state()
    private customerPortalUrl: string | null = null;
    @state()
    private loading = false;
    @state()
    private verfyingSearchAdsCredentials = false;

    @state()
    private apiKeys: Omit<ApiKey, "keyHash">[] = [];
    @state()
    private apiKeysLoading = false;
    @state()
    private apiKeysError: string | null = null;
    @state()
    private showCreateForm = false;
    @state()
    private newKeyName = "";
    @state()
    private creatingKey = false;
    @state()
    private createdKey: { id: string; name: string; key: string } | null = null;
    @state()
    private keyCopied = false;

    private get hasActiveSubscription(): boolean {
        const status = this.user?.subscription?.status;
        return status === "active" || status === "trialing";
    }

    private get keyCount(): number {
        return this.apiKeys.length;
    }

    async connectedCallback() {
        super.connectedCallback();
        this.loading = true;
        const userResponse = await Api.user();
        if (userResponse instanceof Error) {
            window.location.href = "/login";
            return;
        }
        this.user = userResponse;

        const customerPortalUrlResponse = await Api.customerPortalUrl();
        if (!(customerPortalUrlResponse instanceof Error)) {
            this.customerPortalUrl = customerPortalUrlResponse.url;
        }
        this.loading = false;

        if (this.hasActiveSubscription) {
            this.loadApiKeys();
        }
    }

    private async loadApiKeys() {
        this.apiKeysLoading = true;
        this.apiKeysError = null;
        const result = await Api.apiKeysGetAll();
        if (result instanceof Error) {
            this.apiKeysError = "Failed to load API keys.";
        } else {
            this.apiKeys = result;
        }
        this.apiKeysLoading = false;
    }

    private async createApiKey() {
        const name = this.newKeyName.trim();
        if (!name) return;

        this.creatingKey = true;
        const result = await Api.apiKeyCreate(name);
        if (result instanceof Error) {
            this.apiKeysError = "Failed to create API key.";
        } else {
            this.createdKey = result;
            this.keyCopied = false;
            this.showCreateForm = false;
            this.newKeyName = "";
            await this.loadApiKeys();
        }
        this.creatingKey = false;
    }

    private async deleteApiKey(id: string, name: string) {
        if (!window.confirm(`Delete API key "${name}"? This action cannot be undone.`)) return;

        const result = await Api.apiKeyDelete(id);
        if (result instanceof Error) {
            this.apiKeysError = "Failed to delete API key.";
        } else {
            this.createdKey = null;
            await this.loadApiKeys();
        }
    }

    private async copyKeyToClipboard(key: string) {
        try {
            await navigator.clipboard.writeText(key);
            this.keyCopied = true;
        } catch {
            this.apiKeysError = "Failed to copy key to clipboard.";
        }
    }

    private formatDate(date: Date | string | null): string {
        if (!date) return "Never";
        const d = new Date(date);
        return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }

    private async logout() {
        await Api.logout();
        localStorage.clear();
        window.location.href = "/";
    }

    private renderCreatedKeyBanner() {
        if (!this.createdKey) return nothing;

        return html`
            <div class="alert alert-success">
                <div class="flex flex-col gap-2 w-full">
                    <div class="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span class="font-semibold">API key created! Copy it now — it won't be shown again.</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <code class="bg-base-300 px-3 py-1 rounded text-sm break-all flex-1">${this.createdKey.key}</code>
                        <button
                            class="btn btn-sm ${this.keyCopied ? "btn-success" : "btn-ghost"}"
                            @click=${() => this.copyKeyToClipboard(this.createdKey!.key)}
                        >
                            ${this.keyCopied ? "Copied!" : "Copy"}
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    private renderCreateForm() {
        if (!this.showCreateForm) return nothing;

        return html`
            <div class="flex items-center gap-2">
                <input
                    type="text"
                    placeholder="Key name (e.g. CI pipeline)"
                    class="input input-bordered w-full max-w-xs"
                    .value=${this.newKeyName}
                    @input=${(e: Event) => (this.newKeyName = (e.target as HTMLInputElement).value)}
                    @keydown=${(e: KeyboardEvent) => {
                        if (e.key === "Enter") this.createApiKey();
                        if (e.key === "Escape") {
                            this.showCreateForm = false;
                            this.newKeyName = "";
                        }
                    }}
                    ?disabled=${this.creatingKey}
                    maxlength="100"
                />
                <button class="btn btn-primary btn-sm" @click=${this.createApiKey} ?disabled=${this.creatingKey || !this.newKeyName.trim()}>
                    ${this.creatingKey ? html`<span class="loading loading-spinner loading-xs"></span>` : "Create"}
                </button>
                <button
                    class="btn btn-ghost btn-sm"
                    @click=${() => {
                        this.showCreateForm = false;
                        this.newKeyName = "";
                    }}
                    ?disabled=${this.creatingKey}
                >
                    Cancel
                </button>
            </div>
        `;
    }

    private renderApiKeysTable() {
        if (this.apiKeys.length === 0) {
            return html`<p class="text-base-content/60">No API keys yet.</p>`;
        }

        return html`
            <div class="overflow-x-auto">
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Created</th>
                            <th>Last Used</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this.apiKeys.map(
                            (key) => html`
                                <tr>
                                    <td class="font-medium">${key.name}</td>
                                    <td>${this.formatDate(key.created)}</td>
                                    <td>${this.formatDate(key.lastUsed)}</td>
                                    <td>
                                        <button class="btn btn-error btn-xs btn-outline" @click=${() => this.deleteApiKey(key.id, key.name)}>
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            `
                        )}
                    </tbody>
                </table>
            </div>
        `;
    }

    private renderApiKeysSection() {
        if (!this.hasActiveSubscription) return nothing;

        const maxReached = this.keyCount >= MAX_API_KEYS;

        return html`
            <h2>API Keys</h2>
            <div class="flex flex-col space-y-4">
                <p class="text-base-content/60">
                    Use API keys to access the AppShovel API from CLI tools, scripts, and integrations.
                    <a href="/docs/api" class="link link-primary">View API documentation →</a>
                </p>

                ${this.apiKeysError ? html`<div class="alert alert-error"><span>${this.apiKeysError}</span></div>` : nothing}
                ${this.renderCreatedKeyBanner()}
                ${this.apiKeysLoading
                    ? html`<span class="loading loading-spinner loading-md"></span>`
                    : html`
                          ${this.renderApiKeysTable()}

                          <div>
                              ${this.showCreateForm
                                  ? this.renderCreateForm()
                                  : html`
                                        <button
                                            class="btn w-full max-w-xs"
                                            ?disabled=${maxReached}
                                            @click=${() => {
                                                this.showCreateForm = true;
                                                this.createdKey = null;
                                            }}
                                        >
                                            Create API Key
                                        </button>
                                        ${maxReached
                                            ? html`<p class="text-sm text-base-content/60 mt-1">Maximum of ${MAX_API_KEYS} keys reached.</p>`
                                            : nothing}
                                    `}
                          </div>
                      `}
            </div>
        `;
    }

    render() {
        return html`
            <div class="drawer lg:drawer-open">
                <input id="my-drawer" type="checkbox" class="drawer-toggle" />

                <div class="drawer-content">
                    <dashboard-navbar-component></dashboard-navbar-component>
                    <div class="container p-4 md:p-6 lg:p-8 space-y-4">
                        <h1>Settings</h1>
                        <h2>Account</h2>
                        <div class="flex flex-col space-y-2">
                            <p>${this.user?.email}</p>
                            ${this.customerPortalUrl
                                ? html`
                                      <p>
                                          Subscription:
                                          ${this.user?.subscription?.status?.charAt(0).toUpperCase() + this.user?.subscription?.status?.slice(1)}
                                      </p>
                                      <a class="btn w-full max-w-xs" ?disabled=${this.loading} href="${this.customerPortalUrl}" target="_blank"
                                          >Manage Subscription</a
                                      >
                                  `
                                : html`
                                      <p>Subscription: Not Subscribed</p>
                                      <a class="btn w-full max-w-xs" ?disabled=${this.loading} href="/pricing">Open Pricing</a>
                                  `}
                            <button class="btn btn-error w-full max-w-xs" @click=${this.logout}>Logout</button>
                        </div>

                        ${this.renderApiKeysSection()}
                    </div>
                </div>

                <div class="drawer-side">
                    <label for="my-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                    <dashboard-drawer-component></dashboard-drawer-component>
                </div>
            </div>
        `;
    }
}
